import { useQueryClient } from '@tanstack/react-query';
import { useAuthActions } from '@frontegg/react';
import { useState, useMemo, useEffect } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';

import Typography from '~/shared/components/Typography';
import { useAuthContext } from '~/shared/context/useAuthContext';
import { Account, AccountNode, HierarchyResult } from '~/shared/utils/types';
import {
  TENANT_ID_STORAGE_KEY,
  CONFIG_REDIRECT_PENDING_KEY,
  createAccountSpecificQueryPredicate,
} from '~/shared/context/AuthContext';

import SearchAccount from './SearchAccount';

export default function AccountSwitcher() {
  const queryClient = useQueryClient();
  const [, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { switchTenant } = useAuthActions();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const { accounts, activeTenantId } = useAuthContext();

  const hierarchy = useMemo<HierarchyResult>(() => {
    if (!accounts || accounts.length === 0) {
      return { organizations: [], standaloneAccounts: [] };
    }

    const nodeMap = new Map<string, AccountNode>();
    accounts.forEach((account) => {
      nodeMap.set(account.accountId, { ...account, children: [] });
    });

    const childrenIds = new Set<string>();

    accounts.forEach((account) => {
      if (account.parentAccountId && account.parentAccountId !== account.accountId) {
        const parent = nodeMap.get(account.parentAccountId);
        const child = nodeMap.get(account.accountId);

        if (parent && child) {
          parent.children.push(child);
          childrenIds.add(account.accountId);
        }
      }
    });

    const organizations: AccountNode[] = [];
    const standaloneAccounts: Account[] = [];

    const parentAccountIds = new Set(
      accounts.map((a) => a.parentAccountId).filter((id): id is string => Boolean(id))
    );

    nodeMap.forEach((node) => {
      if (childrenIds.has(node.accountId)) {
        return;
      }

      if (parentAccountIds.has(node.accountId)) {
        node.children.sort((a, b) => a.accountName.localeCompare(b.accountName));
        organizations.push(node);
      } else {
        standaloneAccounts.push(node);
      }
    });

    organizations.sort((a, b) => a.accountName.localeCompare(b.accountName));

    const activeOrgIndex = organizations.findIndex(
      (org) =>
        org.accountId === activeTenantId ||
        org.children.some((child) => child.accountId === activeTenantId)
    );

    if (activeOrgIndex !== -1) {
      const [activeOrg] = organizations.splice(activeOrgIndex, 1);
      organizations.unshift(activeOrg);
    }

    standaloneAccounts.sort((a, b) => a.accountName.localeCompare(b.accountName));

    const activeStandaloneIndex = standaloneAccounts.findIndex(
      (account) => account.accountId === activeTenantId
    );
    if (activeStandaloneIndex !== -1) {
      const [activeStandalone] = standaloneAccounts.splice(activeStandaloneIndex, 1);
      standaloneAccounts.unshift(activeStandalone);
    }

    const result = {
      organizations,
      standaloneAccounts,
    };

    return result;
  }, [accounts, activeTenantId]);

  useEffect(() => {
    if (!activeTenantId || !hierarchy.organizations.length) {
      return;
    }

    const activeOrg = hierarchy.organizations.find(
      (org) =>
        org.accountId === activeTenantId ||
        org.children.some((child) => child.accountId === activeTenantId)
    );

    if (activeOrg) {
      setExpandedIds((prev) => {
        if (prev.size > 0) {
          return prev;
        }
        return new Set([activeOrg.accountId]);
      });
    }
  }, [activeTenantId, hierarchy.organizations]);

  useEffect(() => {
    setExpandedIds(new Set());
  }, [searchTerm]);

  const displayData = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const hasSearch = normalizedSearch.length > 0;

    type OrgWithExpansion = AccountNode & {
      isExpanded: boolean;
      matchedChildren: Account[];
    };

    const processedOrgs: OrgWithExpansion[] = [];

    hierarchy.organizations.forEach((org) => {
      const orgMatches = org.accountName.trim().toLowerCase().includes(normalizedSearch);
      const matchedChildren = hasSearch
        ? org.children.filter((child) =>
            child.accountName.trim().toLowerCase().includes(normalizedSearch)
          )
        : org.children;

      const hasMatchingChild = matchedChildren.length > 0;
      const shouldInclude = !hasSearch || orgMatches || hasMatchingChild;

      if (!shouldInclude) {
        return;
      }

      const isExpanded = hasSearch && hasMatchingChild ? true : expandedIds.has(org.accountId);

      processedOrgs.push({
        ...org,
        isExpanded,
        matchedChildren,
      });
    });

    const filteredStandalones = hasSearch
      ? hierarchy.standaloneAccounts.filter((account) =>
          account.accountName.trim().toLowerCase().includes(normalizedSearch)
        )
      : hierarchy.standaloneAccounts;

    const result = {
      organizations: processedOrgs,
      standaloneAccounts: filteredStandalones,
    };

    return result;
  }, [hierarchy, searchTerm, expandedIds]);

  const onSwitchAccount = (id: string): void => {
    const isConfigNode =
      location.pathname.startsWith('/settings/configurations/') &&
      location.pathname !== '/settings/configurations';
    if (isConfigNode) {
      navigate('/settings/configurations');
      sessionStorage.setItem(CONFIG_REDIRECT_PENDING_KEY, '1');
    }
    setSearchParams({});

    // Update localStorage BEFORE switchTenant to ensure proper multi-tab sync and initialization
    localStorage.setItem(TENANT_ID_STORAGE_KEY, id);

    // Clear cache IMMEDIATELY before switchTenant to prevent stale data flash
    const removedQueries: string[] = [];
    queryClient.removeQueries({
      predicate: createAccountSpecificQueryPredicate(removedQueries),
    });

    queryClient.invalidateQueries({ queryKey: ['getTenants'] });
    queryClient.invalidateQueries({ queryKey: ['getAccounts'] });

    switchTenant({ tenantId: id });
  };

  const toggleOrgExpansion = (orgId: string): void => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(orgId)) {
        newSet.delete(orgId);
      } else {
        newSet.add(orgId);
      }
      return newSet;
    });
  };

  return (
    <div className="flex flex-col gap-[7px] w-full">
      <Typography
        variant="p2"
        fontWeight={600}
        color="text-[#949EAC] text-sm leading-6 uppercase tracking-[-0.4px] font-family-inter"
      >
        SWITCH ACCOUNT
      </Typography>

      <SearchAccount
        searchTerm={searchTerm}
        onSearchChange={(term: string) => {
          setSearchTerm(term);
        }}
        placeholder="Search account"
        className="w-full h-12"
      />

      <div className="flex flex-col pr-1 w-full mt-2 max-h-[386px] overflow-auto custom-account-scrollbar">
        {displayData.organizations.length > 0 || displayData.standaloneAccounts.length > 0 ? (
          <>
            {displayData.organizations.map((org) => (
              <div key={`org-${org.accountId}`} className="mb-0.5">
                <div className="flex items-center justify-between h-10 px-4 cursor-default select-none">
                  <Typography variant="p2" fontWeight={500} color="text-gray-700">
                    {org.accountName}
                  </Typography>

                  {org.children.length > 0 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleOrgExpansion(org.accountId);
                      }}
                      className="flex items-center justify-center w-6 h-6 hover:bg-gray-100 rounded transition-all duration-200"
                      aria-label={org.isExpanded ? 'Collapse organization' : 'Expand organization'}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        className={`transition-transform duration-200 text-gray-600 ${
                          org.isExpanded ? 'rotate-180' : 'rotate-0'
                        }`}
                      >
                        <path
                          d="M4 6L8 10L12 6"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  )}
                </div>

                {org.isExpanded &&
                  org.matchedChildren.map((child) => {
                    const isActive = child.accountId === activeTenantId;
                    return (
                      <div
                        key={`child-${child.accountId}`}
                        className={`flex items-center h-10 px-4 pl-8 rounded cursor-pointer transition-colors duration-150 ${
                          isActive
                            ? 'bg-[#EFF4FF] text-[#0052CC]'
                            : 'text-gray-800 hover:bg-gray-50'
                        }`}
                        onClick={() => !isActive && onSwitchAccount(child.accountId)}
                      >
                        <Typography
                          variant="p2"
                          fontWeight={isActive ? 600 : 400}
                          color={isActive ? 'text-[#0052CC]' : 'text-gray-800'}
                        >
                          {child.accountName}
                        </Typography>
                      </div>
                    );
                  })}
              </div>
            ))}

            {displayData.standaloneAccounts.map((account) => {
              const isActive = account.accountId === activeTenantId;
              return (
                <div
                  key={`standalone-${account.accountId}`}
                  className={`flex items-center h-10 px-4 rounded cursor-pointer transition-colors duration-150 mb-0.5 ${
                    isActive ? 'bg-[#EFF4FF] text-[#0052CC]' : 'text-gray-800 hover:bg-gray-50'
                  }`}
                  onClick={() => !isActive && onSwitchAccount(account.accountId)}
                >
                  <Typography
                    variant="p2"
                    fontWeight={isActive ? 600 : 400}
                    color={isActive ? 'text-[#0052CC]' : 'text-gray-800'}
                  >
                    {account.accountName}
                  </Typography>
                </div>
              );
            })}
          </>
        ) : (
          <div className="text-center py-2 text-gray-500">
            {searchTerm !== '' ? 'No matching accounts' : 'No accounts available'}
          </div>
        )}
      </div>
    </div>
  );
}
